/**
 * iter179 — ModuleConfigContext (Access + Lock schema).
 *
 * Fetches the Admin Panel's module config from `GET /api/config/modules`
 * (proxied by the backend). Every module carries:
 *
 *     {access: 'enabled'|'disabled', lock: 'locked'|'unlocked'}
 *
 * The context re-fetches:
 *   • Once on mount
 *   • Every 5 min while the tab is visible
 *   • On every route change (so a fresh property_id reflects instantly)
 *   • Whenever `selectedProperty?.id` changes (property switch)
 *
 * Consumers (bottom nav, guards, profile menu) rely on:
 *
 *     const { isVisible, isLocked, isUsable, getStatus } = useModuleConfig();
 *     isVisible('rate_inventory')  // access === 'enabled'
 *     isLocked('chats')            // lock   === 'locked'
 *     isUsable('booking')          // enabled AND unlocked
 *
 * Fail-open: while loading OR on network error, every module is treated
 * as `access:enabled`, `lock:unlocked` — the App never accidentally
 * hides itself on a config outage.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';

const REFRESH_MS = 5 * 60 * 1000; // 5 minutes
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter139 — Module status codes (a superset of the boolean access flag).
const MODULE_STATUS = {
  ENABLED: 'enabled',
  COMING_SOON: 'coming_soon',
  DISABLED: 'disabled',
};

// Convert `{modules: {<mod>: {access, lock}}, <mod>_enabled: bool, ...}`
// into a plain map keyed by module name.
const buildModulesMap = (payload) => {
  if (!payload || typeof payload !== 'object') return {};
  // New shape: nested `modules` map.
  if (payload.modules && typeof payload.modules === 'object') {
    return payload.modules;
  }
  // Legacy: derive from flat `<mod>_enabled` booleans.
  const out = {};
  Object.keys(payload).forEach((k) => {
    if (typeof k !== 'string') return;
    if (k.endsWith('_enabled') && typeof payload[k] === 'boolean') {
      const name = k.slice(0, -'_enabled'.length);
      out[name] = {
        access: payload[k] ? 'enabled' : 'disabled',
        lock: 'unlocked',
      };
    }
  });
  return out;
};

const ModuleConfigContext = createContext({
  modules: {},
  loading: true,
  refresh: () => {},
  isVisible: () => true,
  isLocked: () => false,
  isUsable: () => true,
  // Back-compat aliases:
  isEnabled: () => true,
  getStatus: () => MODULE_STATUS.ENABLED,
});

export const ModuleConfigProvider = ({ children }) => {
  const [modules, setModules] = useState({});
  const [rawPayload, setRawPayload] = useState({});
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { selectedProperty } = useAuth();
  const propertyId = selectedProperty?.id || selectedProperty?.property_id || '';

  const fetchConfig = useCallback(async (opts = {}) => {
    try {
      const params = new URLSearchParams();
      if (propertyId) params.set('property_id', propertyId);
      // iter180 — `refresh=1` bypasses the backend 5s cache so admin
      // edits reflect within milliseconds. We force it on property
      // switch and on foreground focus.
      if (opts.force) params.set('refresh', '1');
      const qs = params.toString();
      const url = `${API}/config/modules${qs ? `?${qs}` : ''}`;
      const res = await axios.get(url, { timeout: 8000 });
      const payload = res?.data?.RESULT?.modules;
      if (payload && typeof payload === 'object') {
        setRawPayload(payload);
        setModules(buildModulesMap(payload));
      }
    } catch {
      // fail-open — keep whatever we have (defaults to visible+unlocked)
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    // First load + property switch → force fresh (bypass backend cache).
    fetchConfig({ force: true });
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') fetchConfig();
    }, REFRESH_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchConfig({ force: true });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchConfig]);

  // iter164 — Refetch on route change so admin toggles reflect instantly.
  useEffect(() => {
    fetchConfig();
  }, [location.pathname, fetchConfig]);

  const _get = useCallback((moduleName) => {
    if (!moduleName) return null;
    const name = String(moduleName).toLowerCase().replace(/_enabled$/, '');
    return modules[name] || null;
  }, [modules]);

  const isVisible = useCallback((moduleName) => {
    const cfg = _get(moduleName);
    if (!cfg) return true;                    // fail-open
    return cfg.access !== 'disabled';
  }, [_get]);

  const isLocked = useCallback((moduleName) => {
    const cfg = _get(moduleName);
    if (!cfg) return false;
    return cfg.lock === 'locked';
  }, [_get]);

  const isUsable = useCallback((moduleName) => isVisible(moduleName) && !isLocked(moduleName),
                                [isVisible, isLocked]);

  // iter139 back-compat: aliases so existing consumers keep working.
  const isEnabled = isVisible;

  // iter139 — `chats` is still 'coming_soon' unless the panel says
  // otherwise via an explicit `<mod>_status` on the flat payload.
  const getStatus = useCallback((moduleName) => {
    if (!moduleName) return MODULE_STATUS.ENABLED;
    const name = String(moduleName).toLowerCase();
    const explicit = rawPayload[`${name}_status`];
    if (typeof explicit === 'string' && Object.values(MODULE_STATUS).includes(explicit)) {
      return explicit;
    }
    if (!isVisible(name)) return MODULE_STATUS.DISABLED;
    // iter258 — The `chats` module is now live. Earlier iter139 pinned
    // it to `coming_soon` while the Inbox was behind a feature flag;
    // we now let the Admin Panel's authoritative `access` state win so
    // the real Inbox renders when `isUsable('chats')`. Keeping the
    // explicit `<mod>_status` override above means the panel can
    // still force a surface back to `coming_soon` by posting that
    // field.
    return MODULE_STATUS.ENABLED;
  }, [rawPayload, isVisible]);

  const value = useMemo(
    () => ({ modules, loading, refresh: fetchConfig,
             isVisible, isLocked, isUsable, isEnabled, getStatus }),
    [modules, loading, fetchConfig, isVisible, isLocked, isUsable, isEnabled, getStatus]
  );

  return (
    <ModuleConfigContext.Provider value={value}>
      {children}
    </ModuleConfigContext.Provider>
  );
};

export const useModuleConfig = () => useContext(ModuleConfigContext);

export { MODULE_STATUS };
